import React, { useMemo, useState } from 'react'
import BigNumber from 'bignumber.js'
import { Box, Grid, Group, Text, Tooltip } from '@mantine/core'
import { differenceInDays, format, formatDistanceToNowStrict, isAfter } from 'date-fns'
import { convertMicroDenomToDenom } from '@/atoms/Wallet'
import { useInterval } from '@/hooks'
import { Icon, IconName } from '@/components'
import { STRIDE_CURRENCY } from '@/config'
import { ClaimHistoryRecord } from '../queries'

interface ClaimRecordProps {
  record: ClaimHistoryRecord
}

const icon: Record<ClaimHistoryRecord['type'], IconName> = {
  claim: 'claim',
  stake: 'stake',
  'liquid-stake': 'arrowHorizontal'
}

const label: Record<ClaimHistoryRecord['type'], string> = {
  claim: 'Free Claim',
  stake: 'Staking',
  'liquid-stake': 'Liquid Staking'
}

const AirdropClaimRow: React.FC<ClaimRecordProps> = ({ record }) => {
  const [now, setNow] = useState(() => new Date())

  useInterval(() => {
    setNow(new Date())
  }, 60_000)

  const totalFormatted = useMemo(() => {
    return new BigNumber(convertMicroDenomToDenom(record.total)).decimalPlaces(2).toString()
  }, [record])

  const vestingStartDate = useMemo(() => {
    return new Date(record.startDate)
  }, [record])

  const vestingEndDate = useMemo(() => {
    return new Date(record.endDate)
  }, [record])

  const isUnlocked = useMemo(() => {
    if (record.type === 'claim') return true
    return isAfter(now, vestingEndDate)
  }, [record, now, vestingEndDate])

  const vestingEndDateFormatted = useMemo(() => {
    if (isUnlocked) return ''
    return format(vestingEndDate, 'MMM d y')
  }, [isUnlocked, vestingEndDate])

  const vestingDurationFormatted = useMemo(() => {
    if (isUnlocked) return ''
    return formatDistanceToNowStrict(vestingEndDate)
  }, [isUnlocked, vestingEndDate])

  const isNewEntry = useMemo(() => {
    return differenceInDays(now, vestingStartDate) <= 0
  }, [now, vestingStartDate])

  return (
    <Grid>
      <Grid.Col span={5}>
        <Group spacing="xs">
          <Box
            sx={(t) => ({
              height: t.other.airdropClaim.newEntrySize,
              width: t.other.airdropClaim.newEntrySize,
              background: isNewEntry ? t.colors.pink[6] : 'transparent',
              borderRadius: '50%'
            })}
          />
          <Text sx={(t) => ({ color: t.colors.gray[7] })}>{format(vestingStartDate, 'MMM d y')}</Text>
        </Group>
      </Grid.Col>

      <Grid.Col span={4}>
        <Group spacing="xs">
          <Tooltip label={label[record.type]}>
            <Icon name={icon[record.type]} size="xs" sx={(t) => ({ color: t.colors.gray[7] })} />
          </Tooltip>
          <Text sx={(t) => ({ color: t.colors.gray[7] })}>
            <strong>
              {totalFormatted} {STRIDE_CURRENCY.coinDenom}
            </strong>
          </Text>
        </Group>
      </Grid.Col>

      <Grid.Col span={3}>
        <Group position="apart" spacing={0} noWrap>
          <Box />

          <Box sx={{ flexShrink: 0 }}>
            {isUnlocked ? (
              <Text align="right" sx={(t) => ({ color: t.colors.gray[7] })}>
                Unlocked
              </Text>
            ) : null}

            {!isUnlocked && (
              <Tooltip
                label={
                  <span>
                    Vested until <strong>{vestingEndDateFormatted}</strong>
                  </span>
                }>
                <Group spacing="xs">
                  <Icon name="lock" size="xs" />

                  <Text align="right" sx={(t) => ({ color: t.colors.gray[7] })}>
                    {vestingDurationFormatted}
                  </Text>
                </Group>
              </Tooltip>
            )}
          </Box>
        </Group>
      </Grid.Col>
    </Grid>
  )
}

export { AirdropClaimRow }
